import { z } from 'zod';

/**
 * Frontend environment configuration schema
 * Validates all required environment variables at startup
 */
const EnvSchema = z.object({
  apiEndpoint: z.string().url('API endpoint must be a valid URL'),
  cognito: z.object({
    userPoolId: z.string().min(1, 'Cognito User Pool ID is required'),
    clientId: z.string().min(1, 'Cognito Client ID is required'),
    domain: z.string().min(1, 'Cognito domain is required'),
    region: z.string().regex(/^[a-z]{2}-[a-z]+-\d{1}$/, 'Invalid AWS region format'),
  }),
});

export type EnvConfig = z.infer<typeof EnvSchema>;

/**
 * Load and validate environment variables
 * Throws an error if validation fails
 */
function loadEnvConfig(): EnvConfig {
  const config = {
    apiEndpoint: import.meta.env.VITE_API_ENDPOINT,
    cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      domain: import.meta.env.VITE_COGNITO_DOMAIN,
      region: import.meta.env.VITE_COGNITO_REGION,
    },
  };

  try {
    return EnvSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors.map(
        (err) => `${err.path.join('.')}: ${err.message}`
      );
      throw new Error(
        `Environment configuration validation failed:\n${errorMessages.join('\n')}`
      );
    }
    throw error;
  }
}

/**
 * Validated environment configuration
 * Use this throughout the application instead of accessing import.meta.env directly
 */
export const env = loadEnvConfig();
